import { ChangeEvent, FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormLabel, Section, SectionContent, Label, GrayedText, LongSectionContent, DataTextLarge } from './Components';

import { getVideoDuration } from '../../services/RemoteTherapyMonitoring';

const SelectWrapper = styled.div`
  display: inline-block;
  position: relative;
  margin-bottom: 12px;

  &::after {
    content: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 21"><path stroke="%235E5E5E" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.4" d="M6 9.5L10 13.5L14 9.5"/></svg>');
    display: block;
    position: absolute;
    height: 20px;
    width: 20px;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    z-index: 2;
    pointer-events: none;
  }
`;

const Select = styled.select(({ theme }) => {
  const { color, type } = theme;

  return `
    font-size: ${type.size.small};
    line-height: 1.3125rem;
    letter-spacing: 0.0025em;
    font-weight: ${type.weight.regular};
    color: ${color.grayscale[0]};
    border-radius: 100px;
    padding: 6px 28px 6px 12px;
    border: 1px solid #C2C2C2;
    background-color: ${color.grayscale[1000]};
    -webkit-appearance: none;
    -moz-appearance: none;
    z-index: 1;
  `;
});

const Loader = styled.div(({ theme }) => {
  return `
    font-size: ${theme.type.size.regular};
    text-align: center;
    width: 100%;
    padding: 17px;
  `;
});

const LastCall = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[600]};
    font-weight: ${type.weight.regular};
    font-size: ${type.size.small};
    line-height: 1.3125rem;
    margin-top: 0.75rem;
  `;
});

interface ConferenceDurationSectionProps {
  playerId: number;
  datesArray: string[];
}

const ConferenceDurationSection: FC<ConferenceDurationSectionProps> = ({ playerId, datesArray }) => {
  const [availableVideoMonths, setAvailableVideoMonths] = useState<string[]>([]);
  const [videoDuration, setVideoDuration] = useState(null);
  const [videoGoal, setVideoGoal] = useState(null);
  const [videoGap, setVideoGap] = useState(null);
  const [lastVideoDate, setLastVideoDate] = useState<string | null>(null);
  const [loadingVideoDuration, setLoadingVideoDuration] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  const getValidMonths = (dates: string[]): string[] => {
    const validDates = dates.filter((date) => !isNaN(Date.parse(date)));

    if (validDates.length === 0) {
      return [];
    }

    const earliestDate = new Date(Math.min(...validDates.map((date) => Date.parse(date))));
    const latestDate = new Date(Math.max(...validDates.map((date) => Date.parse(date))));
    const currentDate = new Date();

    const months: string[] = [];
    const currentMonth = earliestDate;

    while (currentMonth <= latestDate && currentMonth <= currentDate) {
      const monthString = currentMonth.toLocaleString('en-US', { timeZone: 'GMT', month: 'long', year: 'numeric' });
      months.push(monthString);
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    return months.reverse();
  };

  const getDurationData = (filter?: Date) => {
    setLoadingVideoDuration(true);
    let minDate;
    let maxDate;

    if (filter) {
      const date = new Date(filter);
      const month = date.getMonth();
      const year = date.getFullYear();
      minDate = new Date(year, month, 1);
      maxDate = new Date(year, month + 1, 0);
    }

    getVideoDuration(playerId, minDate, maxDate).then((response) => {
      const lastVideoDate = new Date(response.last_record_date);

      setVideoDuration(response.total_duration);
      setLastVideoDate(
        !isNaN(Date.parse(response.last_record_date)) ? lastVideoDate.toLocaleDateString('en-us') : null
      );

      setVideoGoal(response.goal_duration);
      setVideoGap(response.duration_gap);

      setLoadingVideoDuration(false);
    });
  };

  const handleVideoMonthChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const date = new Date(e.target.value);
    getDurationData(date);
  };

  useEffect(() => {
    if (initialLoad && availableVideoMonths.length) {
      getDurationData(new Date(availableVideoMonths[0]));
      setInitialLoad(false);
    }
  }, [availableVideoMonths]);

  useEffect(() => {
    setAvailableVideoMonths(getValidMonths(datesArray));
  }, [datesArray]);

  const optionLoop = availableVideoMonths.map((option) => {
    const date = new Date(option);
    const formatted = date.toLocaleString('en-us', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    return <option value={formatted}>{formatted}</option>;
  });

  const gap = videoGap && videoGap > 0 ? videoGap : 0;

  return (
    <>
      <FormLabel htmlFor="videoDurationMonth" display="inline-block">
        Choose Episode of Care
      </FormLabel>

      <SelectWrapper>
        <Select id="videoDurationMonth" onChange={handleVideoMonthChange}>
          {optionLoop}
        </Select>
      </SelectWrapper>

      {loadingVideoDuration ? (
        <Section>
          <Loader>
            Loading... <i className="fa fa-spin fa-spinner"></i>
          </Loader>
        </Section>
      ) : (
        <>
          <Section>
            <LongSectionContent>
              <Label>Video monitoring duration</Label>

              <DataTextLarge>{videoDuration} minutes</DataTextLarge>
            </LongSectionContent>

            <LongSectionContent>
              <Label>Goal</Label>

              <DataTextLarge>
                {videoGoal} minutes <GrayedText>(per calendar month)</GrayedText>
              </DataTextLarge>
            </LongSectionContent>

            <SectionContent>
              <Label>Gap</Label>

              <DataTextLarge>{gap} minutes</DataTextLarge>
            </SectionContent>
          </Section>

          {lastVideoDate && (
            <Section>
              <LongSectionContent>
                <LastCall>*Last video call: {lastVideoDate}</LastCall>
              </LongSectionContent>
            </Section>
          )}
        </>
      )}
    </>
  );
};

export default ConferenceDurationSection;
